import { useState } from "react";
import {
  Link as LinkIcon,
  Merge,
  MoreVertical,
  PowerOff,
  Power,
  ExternalLink,
  Edit,
  ClipboardCopy,
  Radar,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import { useUserPermissions } from "@/hooks/use-user-permissions";
import { useFreelancersInvalidate } from "@/hooks/use-freelancers-invalidate";
import { useQueryClient } from "@tanstack/react-query";
import { writeTextToClipboard } from "@/utils/clipboard";
import { useCopyFreelancerDetails } from "@/hooks/use-copy-freelancer-details";
import { CopyFallbackDialog } from "@/components/freelancers/CopyFallbackDialog";
import { IndicarAgenciaDialog } from "@/components/freelancers/IndicarAgenciaDialog";
import { EnviarParaVagaDialog } from "@/components/vagas/EnviarParaVagaDialog";
import type { FreelancerCopyData } from "@/utils/freelancer-clipboard";

interface FreelancerActionsMenuProps {
  freelancerId: string;
  freelancerName: string;
  freelancerEmail?: string | null;
  isActive: boolean;
  /** Quando true, oferece um item de menu para abrir o wizard de mesclagem. */
  onMergeClick?: () => void;
  /** Variante visual do botão gatilho. Padrão `ghost` (ícone discreto). */
  triggerVariant?: "ghost" | "outline";
  /** Tamanho do botão gatilho. Padrão `icon`. */
  triggerSize?: "icon" | "sm";
  /** Mostra um item extra "Ver detalhes" que navega via callback fornecido. */
  onViewDetailsClick?: () => void;
  /** Mostra um item "Editar informações" no topo do menu. */
  onEditClick?: () => void;
  /** Dados usados pelo item "Copiar detalhes do freelancer". */
  copyData?: FreelancerCopyData;
  /** Chamado após uma ação completar com sucesso (refetch externo, etc.). */
  onChanged?: () => void;
}

/**
 * Menu de ações para um freelancer (3 pontinhos). Compartilhado entre
 * a página de detalhes e os cards de listagem.
 */
export function FreelancerActionsMenu({
  freelancerId,
  freelancerName,
  freelancerEmail,
  isActive,
  onMergeClick,
  triggerVariant = "ghost",
  triggerSize = "icon",
  onViewDetailsClick,
  onEditClick,
  copyData,
  onChanged,
}: FreelancerActionsMenuProps) {
  const { toast } = useToast();
  const { screens } = useUserPermissions();
  const canMergeFreelancers = screens.includes("mesclar_freelancers");
  const canEnviarParaVaga = screens.includes("vagas");
  const invalidateFreelancers = useFreelancersInvalidate();
  const queryClient = useQueryClient();
  const { copyDetails, fallbackOpen, setFallbackOpen, fallbackText } =
    useCopyFreelancerDetails();

  const [confirmInactivate, setConfirmInactivate] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [indicarOpen, setIndicarOpen] = useState(false);
  const [enviarParaVagaOpen, setEnviarParaVagaOpen] = useState(false);

  const handleCopyJotformLink = async () => {
    if (!freelancerEmail) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Email do freelancer não encontrado.",
      });
      return;
    }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      toast({
        variant: "destructive",
        title: "Erro de autenticação",
        description: "Você precisa estar logado para gerar o link.",
      });
      return;
    }
    const url = `${SUPABASE_URL}jf/${freelancerId}`;
    try {
      await writeTextToClipboard(url);
      toast({
        title: "Link copiado!",
        description:
          "Link do formulário de atualização copiado para a área de transferência.",
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro ao copiar",
        description: (err as Error).message,
      });
    }
  };

  const handleCopyDetails = () => {
    if (!copyData) {
      toast({
        variant: "destructive",
        title: "Sem dados",
        description: "Não há dados do freelancer para copiar.",
      });
      return;
    }
    copyDetails(copyData);
  };

  const handleToggleActive = async () => {
    setIsToggling(true);
    try {
      const newValue = !isActive;
      const { error } = await supabase
        .from("freelancers")
        .update({ is_active: newValue, updated_at: new Date().toISOString() })
        .eq("id", freelancerId);
      if (error) throw error;

      invalidateFreelancers();
      queryClient.invalidateQueries({ queryKey: ["freelancer", freelancerId] });
      queryClient.invalidateQueries({ queryKey: ["freelancers"] });

      toast({
        title: newValue ? "Freelancer reativado" : "Freelancer inativado",
        description: `${freelancerName} agora está ${newValue ? "ativo" : "inativo"}.`,
      });
      onChanged?.();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar status",
        description: (err as Error).message,
      });
    } finally {
      setIsToggling(false);
      setConfirmInactivate(false);
    }
  };

  const triggerClass =
    triggerSize === "icon" ? "h-7 w-7" : undefined;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={triggerVariant}
            size={triggerSize}
            className={triggerClass}
            onClick={(e) => e.stopPropagation()}
            title="Mais ações"
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          onClick={(e) => e.stopPropagation()}
        >
          {onEditClick && (
            <>
              <DropdownMenuItem onSelect={onEditClick}>
                <Edit className="mr-2 h-4 w-4" />
                Editar informações
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {onViewDetailsClick && (
            <>
              <DropdownMenuItem onSelect={onViewDetailsClick}>
                <ExternalLink className="mr-2 h-4 w-4" />
                Ver detalhes
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onSelect={handleCopyJotformLink}>
            <LinkIcon className="mr-2 h-4 w-4" />
            Copiar link do formulário de atualização
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleCopyDetails}>
            <ClipboardCopy className="mr-2 h-4 w-4" />
            Copiar detalhes do freelancer
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setIndicarOpen(true)}>
            <Radar className="mr-2 h-4 w-4" />
            Match com agência...
          </DropdownMenuItem>
          {canEnviarParaVaga && (
            <DropdownMenuItem onSelect={() => setEnviarParaVagaOpen(true)}>
              <Send className="mr-2 h-4 w-4" />
              Enviar para vaga...
            </DropdownMenuItem>
          )}
          {canMergeFreelancers && isActive && onMergeClick && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onMergeClick}>
                <Merge className="mr-2 h-4 w-4" />
                Mesclar freelancer
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          {isActive ? (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setConfirmInactivate(true);
              }}
              className="text-destructive focus:text-destructive"
            >
              <PowerOff className="mr-2 h-4 w-4" />
              Inativar freelancer
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                handleToggleActive();
              }}
              disabled={isToggling}
            >
              <Power className="mr-2 h-4 w-4" />
              Reativar freelancer
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <CopyFallbackDialog
        open={fallbackOpen}
        onOpenChange={setFallbackOpen}
        text={fallbackText}
      />

      <IndicarAgenciaDialog
        open={indicarOpen}
        onOpenChange={setIndicarOpen}
        freelancerId={freelancerId}
        freelancerName={freelancerName}
      />

      <EnviarParaVagaDialog
        open={enviarParaVagaOpen}
        onOpenChange={setEnviarParaVagaOpen}
        freelancers={[{ id: freelancerId, nome: freelancerName }]}
      />

      <AlertDialog open={confirmInactivate} onOpenChange={setConfirmInactivate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar freelancer?</AlertDialogTitle>
            <AlertDialogDescription>
              {freelancerName} será marcado como inativo e deixará de aparecer
              nas listagens padrão. Você pode reverter essa ação a qualquer
              momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isToggling}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleToggleActive();
              }}
              disabled={isToggling}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isToggling ? "Inativando..." : "Inativar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
